import { useEffect, useState } from "react";
import { HOURS, CLOSING_WARNING_MINUTES } from "@/data/site";

// Returns {day, hours, minutes} in Europe/Istanbul timezone regardless of visitor location
function nowInIstanbul() {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Istanbul",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = fmt.formatToParts(new Date());
  const weekdayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const wd = parts.find(p => p.type === "weekday")?.value;
  const hh = parseInt(parts.find(p => p.type === "hour")?.value || "0", 10);
  const mm = parseInt(parts.find(p => p.type === "minute")?.value || "0", 10);
  return { day: weekdayMap[wd] ?? 0, hours: hh, minutes: mm };
}

function computeStatus() {
  const { day, hours, minutes } = nowInIstanbul();
  const todaysIntervals = HOURS[day] || [];
  const hhmm = hours * 60 + minutes;
  const toMin = (s) => { const [h, m] = s.split(":").map(Number); return h * 60 + m; };

  if (todaysIntervals.length === 0) return { state: "closed", label: "Bugün Kapalı" };
  const lastClose = toMin(todaysIntervals[todaysIntervals.length - 1].close);

  for (let i = 0; i < todaysIntervals.length; i++) {
    const { open, close } = todaysIntervals[i];
    const o = toMin(open); const c = toMin(close);
    if (hhmm >= o && hhmm < c) {
      const isLastInterval = i === todaysIntervals.length - 1;
      if (isLastInterval && lastClose - hhmm <= CLOSING_WARNING_MINUTES) {
        return { state: "closing", label: `Kapanıyor · ${close}` };
      }
      return { state: "open", label: `Açık · ${close}'e kadar` };
    }
  }

  const firstOpen = toMin(todaysIntervals[0].open);
  if (hhmm < firstOpen) return { state: "closed", label: `Kapalı · ${todaysIntervals[0].open}'de açılıyor` };
  for (let i = 0; i < todaysIntervals.length - 1; i++) {
    const thisClose = toMin(todaysIntervals[i].close);
    const nextOpen = toMin(todaysIntervals[i + 1].open);
    if (hhmm >= thisClose && hhmm < nextOpen) {
      return { state: "closed", label: `Öğle Arası · ${todaysIntervals[i + 1].open}` };
    }
  }
  return { state: "closed", label: "Kapalı" };
}

export default function WorkingHoursIndicator({ dark = false }) {
  const [status, setStatus] = useState(() => computeStatus());
  useEffect(() => { const t = setInterval(() => setStatus(computeStatus()), 30000); return () => clearInterval(t); }, []);

  const colors = {
    open:    { dot: "bg-[#34C759]", ring: "bg-[#34C759]/40", text: dark ? "text-[#34C759]" : "text-[#34C759]" },
    closing: { dot: "bg-[#FFCC00]", ring: "bg-[#FFCC00]/40", text: dark ? "text-[#FFCC00]" : "text-[#B8860B]" },
    closed:  { dot: "bg-[#FF3B30]", ring: "bg-[#FF3B30]/40", text: dark ? "text-[#FF6B63]" : "text-[#FF3B30]" },
  }[status.state];

  const surface = dark
    ? "bg-white/5 border border-white/10 backdrop-blur"
    : "bg-white/80 backdrop-blur border border-[#E5E5EA]";

  return (
    <div
      data-testid="working-hours-indicator"
      className={`inline-flex items-center gap-2.5 rounded-full px-3 py-1.5 text-xs font-semibold ${surface}`}
    >
      <span className="relative flex h-2.5 w-2.5">
        <span className={`absolute inline-flex h-full w-full rounded-full ${colors.ring} pulse-dot`}></span>
        <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${colors.dot}`}></span>
      </span>
      <span className={colors.text}>{status.label}</span>
    </div>
  );
}
